import { useSyncExternalStore } from 'react';
import en from './locales/en.json';
import uk from './locales/uk.json';
import ru from './locales/ru.json';
import zh from './locales/zh.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import es from './locales/es.json';

export const languages = [{ value: 'en', label: 'English' }, { value: 'uk', label: 'Українська' }, { value: 'ru', label: 'Русский' }, { value: 'zh', label: '中文' }, { value: 'de', label: 'Deutsch' }, { value: 'fr', label: 'Français' }, { value: 'es', label: 'Español' }] as const;
export type Language = typeof languages[number]['value'];
export const catalogs: Record<Language, Record<string, string>> = { en, uk, ru, zh, de, fr, es };
let language: Language = 'en';
try { const saved = localStorage.getItem('registry-v1.language'); if (languages.some(item => item.value === saved)) language = saved as Language; } catch { }
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
let formatting = { locale: language as string, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, unitSystem: 'authored' };
export function useLanguage() { return useSyncExternalStore(subscribe, () => language); }
export function currentLanguage() { return language; }
export function setLanguage(next: string) {
  if (!languages.some(item => item.value === next)) return;
  language = next as Language;
  document.documentElement.lang = language;
  document.title = t('Engineering Registry');
  try { localStorage.setItem('registry-v1.language', language); } catch { }
  listeners.forEach(listener => listener());
}
export function setFormatting(values: typeof formatting) { formatting = values; }
export function t(source: string, values: Record<string, string | number> = {}): string {
  const key = source.trim();
  const text = catalogs[language][key] ?? catalogs.en[key] ?? key;
  return source.slice(0, source.indexOf(key)) + text.replace(/\{([A-Za-z0-9_]+)\}/g, (match, name: string) => String(values[name] ?? match)) + source.slice(source.indexOf(key) + key.length);
}
export function translateMessage(source: string): string {
  if (catalogs.en[source.trim()] !== undefined) return t(source);
  for (const key of Object.keys(catalogs.en)) {
    if (!key.includes('{')) continue;
    const names: string[] = [];
    const pattern = key.split(/(\{[A-Za-z0-9_]+\})/).map(fragment => {
      if (/^\{[A-Za-z0-9_]+\}$/.test(fragment)) { names.push(fragment.slice(1, -1)); return '(.+?)'; }
      return fragment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }).join('');
    const match = source.match(new RegExp(`^${pattern}$`));
    if (match) return t(key, Object.fromEntries(names.map((name, index) => [name, name === 'field' ? t(match[index + 1]) : match[index + 1]])));
  }
  return source;
}
export function formatDate(value: string, dateOnly = false) {
  return new Intl.DateTimeFormat(formatting.locale, { timeZone: formatting.timeZone, dateStyle: 'medium', ...(dateOnly ? {} : { timeStyle: 'short' as const }) }).format(new Date(value));
}
export function formatNumber(value: number | string) {
  const formatter = new Intl.NumberFormat(formatting.locale, { maximumFractionDigits: 6 });
  if (typeof value === 'number') return formatter.format(value);
  const match = value.match(/^(-?\d+)(?:\.(\d{1,6}))?$/);
  if (!match) return formatter.format(Number(value));
  let integer = formatter.format(BigInt(match[1]));
  if (match[1].startsWith('-') && BigInt(match[1]) === 0n) integer = formatter.format(-0);
  const fraction = match[2]?.replace(/0+$/, '');
  if (!fraction) return integer;
  const decimal = formatter.formatToParts(1.1).find(part => part.type === 'decimal')!.value;
  const digitFormatter = new Intl.NumberFormat(formatting.locale, { useGrouping: false });
  return integer + decimal + [...fraction].map(digit => digitFormatter.format(Number(digit))).join('');
}
const measures: Record<string, { dimension: 'length' | 'mass'; scale: number }> = { mm: { dimension: 'length', scale: .001 }, cm: { dimension: 'length', scale: .01 }, m: { dimension: 'length', scale: 1 }, in: { dimension: 'length', scale: .0254 }, ft: { dimension: 'length', scale: .3048 }, g: { dimension: 'mass', scale: .001 }, kg: { dimension: 'mass', scale: 1 }, lb: { dimension: 'mass', scale: .45359237 } };
export function formatQuantity(quantity: number | string, unit: string) {
  const measure = measures[unit];
  if (formatting.unitSystem === 'authored' || !measure) return `${formatNumber(quantity)} ${unit}`;
  const target = measure.dimension === 'length' ? formatting.unitSystem === 'metric' ? 'mm' : 'in' : formatting.unitSystem === 'metric' ? 'kg' : 'lb';
  return `${formatNumber(Number(quantity) * measure.scale / measures[target].scale)} ${target}`;
}
